import { twMerge } from 'tailwind-merge';
import type { DecoratedOrderTask } from '@witmetrics/utils';
import { useDispatch } from '@witmetrics/data-store';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { usePath } from '@/hooks/usePath';
import { CardListItem } from '@/components/List';
import DeleteTaskDialog from '@/dialogs/DeleteTaskDialog';
import OrderTaskDialog from '@/dialogs/OrderTaskDialog';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import CheckBox from '@/components/CheckBox';
import TaskNotes from './TaskNotes';
import OrderTaskHeader from './OrderTaskHeader';
import { classes, isHighlighted } from './utils';

export type OrderTaskListItemProps = {
  condensed?: boolean;
  onlyTime?: boolean;
  className?: string;
  orderTask: DecoratedOrderTask;
};

export default function OrderTaskListItem({
  condensed,
  onlyTime,
  className,
  orderTask,
}: OrderTaskListItemProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const { searchParams } = usePath();
  const [isEditDialogShown, toggleEditDialog] = useToggle();
  const [isDeleteWarningShown, toggleDeleteWarning] = useToggle();
  const [isUpdating, toggleUpdating] = useToggle();
  const { task, order } = orderTask;

  const handleEditTask = () => {
    toggleEditDialog(true);
  };

  const handleCancelEdit = () => {
    toggleEditDialog(false);
  };

  const handleShowDeleteWarning = () => {
    toggleDeleteWarning();
  };

  const handleToggleChecked = async (isResolved: boolean) => {
    try {
      toggleUpdating(true);
      dispatch.addTasks([await API.Tasks.updateTask(task.id, { isResolved })]);
      toggleUpdating(false);
    } catch (err) {
      toggleUpdating(false);
      onApiError(err, 'Error saving task');
    }
  };

  return (
    <>
      {isEditDialogShown && (
        <OrderTaskDialog
          taskId={task.id}
          orderId={order.id}
          onClose={handleCancelEdit}
        />
      )}
      {isDeleteWarningShown && (
        <DeleteTaskDialog
          taskId={task.id}
          onClose={() => toggleDeleteWarning()}
        />
      )}
      <CardListItem
        className={twMerge(
          classes.card,
          isHighlighted(orderTask, searchParams) && classes.highlighted,
          className
        )}
        renderIcon={() => (
          <CheckBox
            isUpdating={isUpdating}
            isChecked={task.isResolved}
            name="isResolved"
            variant="light"
            onChange={({ isResolved }) => handleToggleChecked(isResolved)}
          />
        )}
        renderMenu={(open, ref, onClose) => (
          <DefaultOptionsMenu
            anchorTo={ref}
            open={open}
            onEdit={handleEditTask}
            onDelete={handleShowDeleteWarning}
            onClose={onClose}
          />
        )}>
        <div className={classes.content}>
          <OrderTaskHeader
            condensed={condensed}
            onlyTime={onlyTime}
            orderTask={orderTask}
          />
          <TaskNotes task={task} />
        </div>
      </CardListItem>
    </>
  );
}
